import { createContext, useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CreateFileData } from "../files.types";
import { Uploader } from "../Uploader";
import type { UploaderApi } from "../Uploader";
import { sumSizes } from "../utils";
import { httpKeepAlive } from "@/api/connection";
import { SystemQuery } from "@/Features/System";

export type FileUploaderFile = CreateFileData & {
  file: File;
  /**
   * Routes for this file's upload, when they aren't the `/files/*` defaults.
   * A file dropped into an evidence item folder uploads through the evidence
   * photo routes (ENG-1446). Never sent as metadata.
   */
  api?: UploaderApi;
  /** Called once this file's upload finishes. */
  onUploaded?: (result: unknown) => void;
};

export type FileUploaderContextType = {
  addFiles: (files: FileUploaderFile[]) => void;
  uploading: boolean;
  files: FileUploaderFile[];
  uploadQueue: FileUploaderFile[];
  currentFile: FileUploaderFile | null;
  clearFiles: () => void;
  hashSettings: {
    hashingEnabled: boolean;
  };
  updateHashSettings: (settings: { hashingEnabled: boolean }) => void;
  /**
   * Bytes queued but not yet started server-side, plus bytes reserved by a
   * storage-limit check that passed but whose batch `addFiles` has not queued
   * yet. Reads live refs, so it is safe to call from a closure captured
   * earlier (e.g. a file picker's `onchange`).
   */
  getPendingBytes: () => number;
  /**
   * Count a batch that passed the storage-limit check as pending until the
   * caller hands it to `addFiles`, which releases the reservation. Callers
   * must queue the same batch synchronously after a passed check.
   */
  reservePendingBytes: (bytes: number) => void;
};

export const FileUploaderContext = createContext<FileUploaderContextType>({
  addFiles: () => {},
  uploading: false,
  files: [],
  uploadQueue: [],
  currentFile: null,
  clearFiles: () => {},
  hashSettings: {
    hashingEnabled: true,
  },
  updateHashSettings: () => {},
  getPendingBytes: () => 0,
  reservePendingBytes: () => {},
});

export const FileUploaderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [files, setFiles] = useState<FileUploaderFile[]>([]);
  const [hashSettings, setHashSettings] = useState<{
    hashingEnabled: boolean;
  }>({
    hashingEnabled: localStorage.getItem("hashSettings") === "true",
  });
  const [uploadQueue, setUploadQueue] = useState<FileUploaderFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState<FileUploaderFile | null>(null);
  const uploadQueueRef = useRef<FileUploaderFile[]>([]);
  const reservedBytesRef = useRef(0);
  const queueChangedSinceRefreshRef = useRef(false);
  const queryClient = useQueryClient();

  const getPendingBytes = useCallback(
    () => sumSizes(uploadQueueRef.current) + reservedBytesRef.current,
    []
  );

  const reservePendingBytes = useCallback((bytes: number) => {
    reservedBytesRef.current += Math.max(0, bytes);
  }, []);

  const addFiles = (files: FileUploaderFile[]) => {
    // The batch is now in the queue, so it no longer needs a reservation
    reservedBytesRef.current = Math.max(
      0,
      reservedBytesRef.current - sumSizes(files)
    );
    setUploading(true);
    setFiles((prev) => [...prev, ...files]);
    setUploadQueue((prev) => {
      const newQueue = [...prev, ...files];
      uploadQueueRef.current = newQueue;
      return newQueue;
    });
  };

  const clearFiles = () => {
    setFiles([]);
    setUploadQueue([]);
    uploadQueueRef.current = [];
    reservedBytesRef.current = 0;
  };

  const uploadNextFile = () => {
    if (uploadQueueRef.current.length === 0) {
      setUploading(false);
      // Keep the Settings → System storage meter in step with the new total.
      // Once per drained queue rather than per file: the deployment-alerts
      // observer on this key is always mounted, so every invalidation is a
      // network request. Failed files count too: the server sums file_size
      // over FAILED rows as well.
      if (queueChangedSinceRefreshRef.current) {
        queueChangedSinceRefreshRef.current = false;
        queryClient.invalidateQueries({ queryKey: SystemQuery.queryKeys.info });
      }
      return;
    }

    const nextFile = uploadQueueRef.current.shift();
    if (!nextFile) {
      setUploading(false);
      return;
    }

    // Update the state to reflect the removed file
    setUploadQueue((prev) => {
      const newQueue = prev.filter((file) => file.uuid !== nextFile.uuid);
      uploadQueueRef.current = newQueue;
      return newQueue;
    });

    setCurrentFile(nextFile);

    const { file, api, onUploaded, ...metadata } = nextFile;
    httpKeepAlive();
    new Uploader({
      file,
      metadata,
      api,
      hashFile: hashSettings.hashingEnabled,
      onComplete: (result) => {
        onUploaded?.(result);
        const event = new CustomEvent("file-uploader:file-progress", {
          detail: {
            type: "complete",
            uuid: metadata.uuid,
          },
        });
        document.dispatchEvent(event);
        queueChangedSinceRefreshRef.current = true;
        setCurrentFile(null);
        uploadNextFile();
      },
      onProgress: (progress) => {
        const event = new CustomEvent("file-uploader:file-progress", {
          detail: progress,
        });
        document.dispatchEvent(event);
      },
      onError: (error) => {
        const event = new CustomEvent("file-uploader:file-progress", {
          detail: { type: "error", uuid: metadata.uuid },
        });
        document.dispatchEvent(event);
        queueChangedSinceRefreshRef.current = true;
        setCurrentFile(null);
        uploadNextFile();
      },
    });
  };

  const updateHashSettings = (settings: { hashingEnabled: boolean }) => {
    localStorage.setItem("hashSettings", JSON.stringify(settings));
    setHashSettings(settings);
  };

  useEffect(() => {
    if (!currentFile) {
      uploadNextFile();
    }
  }, [uploadQueue, hashSettings.hashingEnabled]);

  return (
    <FileUploaderContext.Provider
      value={{
        addFiles,
        uploading,
        files,
        uploadQueue,
        currentFile,
        clearFiles,
        hashSettings,
        updateHashSettings,
        getPendingBytes,
        reservePendingBytes,
      }}
    >
      {children}
    </FileUploaderContext.Provider>
  );
};
